import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useAppContext } from '@/Main';
import { rgbaColor } from '@/helpers/utils';
import { PANEL_FONT_FAMILY } from '../../utils/panel-font';
import {
  DASHBOARD_PLOT_HEIGHT,
  chartChrome,
  escapeHtml,
  themePrimary,
} from '../../utils/competition-chart';
import { DashboardEmptyNote } from './DashboardEmptyNote';
import { ChartLegend, useLegendHidden } from './competition/ChartLegend';
import type {
  CompetitorInsight,
  ProjectVisibilityOwnBrand,
  ProjectVisibilityTopBrand,
} from '../../types';

interface ShareOfVoiceRow {
  key: string;
  name: string;
  shareOfVoice: number;
  isFocus: boolean;
}

const normalizeHost = (value: string | null | undefined) =>
  (value ?? '').toLowerCase().replace(/^www\./, '');

function matchesTracked(row: Pick<ProjectVisibilityTopBrand, 'canonicalName' | 'domain'>, tracked: CompetitorInsight) {
  const name = row.canonicalName.toLowerCase();
  const host = normalizeHost(row.domain);
  return (
    name === tracked.brandName.toLowerCase() ||
    tracked.aliases.some((alias) => alias.name.toLowerCase() === name) ||
    (host !== '' && host === normalizeHost(tracked.host))
  );
}

function isFocusRow(row: { key: string; name: string }, focusKey: string | null | undefined): boolean {
  if (!focusKey) return false;
  return row.key === focusKey || row.name === focusKey;
}

function buildRows(
  ownBrand: Pick<ProjectVisibilityOwnBrand, 'canonicalName' | 'shareOfVoice'> | null,
  competitors: Pick<ProjectVisibilityTopBrand, 'knownBrandId' | 'canonicalName' | 'domain' | 'shareOfVoice'>[],
  trackedCompetitors: CompetitorInsight[],
  focusKey?: string | null,
): ShareOfVoiceRow[] {
  const rows: ShareOfVoiceRow[] = [
    ...(ownBrand
      ? [
          {
            key: 'own',
            name: ownBrand.canonicalName ?? '',
            shareOfVoice: ownBrand.shareOfVoice,
            isFocus: !focusKey,
          },
        ]
      : []),
    ...competitors.map((row) => {
      const item = {
        key: row.knownBrandId ?? row.canonicalName,
        name: row.canonicalName,
        shareOfVoice: row.shareOfVoice,
      };
      return { ...item, isFocus: isFocusRow(item, focusKey) };
    }),
    ...trackedCompetitors
      .filter((tracked) => !competitors.some((row) => matchesTracked(row, tracked)))
      .map((tracked) => {
        const item = {
          key: tracked.id,
          name: tracked.brandName,
          shareOfVoice: 0,
        };
        return { ...item, isFocus: isFocusRow(item, focusKey) };
      }),
  ];
  return rows.sort((a, b) => b.shareOfVoice - a.shareOfVoice);
}

function barFill(share: number, maxShare: number, primary: string): string {
  const t = maxShare <= 0 ? 0 : share / maxShare;
  return rgbaColor(primary, 0.22 + 0.78 * t);
}

/** Share-of-voice bars for own brand + tracked competitors (backend-computed `shareOfVoice`). */
export function ShareOfVoiceChart({
  ownBrand,
  competitors,
  trackedCompetitors,
  plotHeight = DASHBOARD_PLOT_HEIGHT,
  focusKey,
}: {
  ownBrand: Pick<ProjectVisibilityOwnBrand, 'canonicalName' | 'shareOfVoice'> | null;
  competitors: Pick<ProjectVisibilityTopBrand, 'knownBrandId' | 'canonicalName' | 'domain' | 'shareOfVoice'>[];
  trackedCompetitors: CompetitorInsight[];
  plotHeight?: number;
  /** When set, this brand is bold instead of the own brand (domain filter). */
  focusKey?: string | null;
}) {
  const {
    config: { isDark, themeVariant },
  } = useAppContext() as { config: { isDark: boolean; themeVariant?: string } };

  const { hidden, toggle } = useLegendHidden();
  const rows = useMemo(
    () => buildRows(ownBrand, competitors, trackedCompetitors, focusKey),
    [ownBrand, competitors, trackedCompetitors, focusKey],
  );
  const visibleRows = useMemo(
    () => rows.filter((row) => !hidden.has(row.key)),
    [rows, hidden],
  );

  const option = useMemo(() => {
    if (!visibleRows.length) return null;
    const chrome = chartChrome();
    const primary = themePrimary();
    const maxShare = Math.max(0, ...rows.map((row) => row.shareOfVoice));
    const axisMax = Math.min(100, Math.max(10, Math.ceil(maxShare / 5) * 5));
    // Category y-axis draws bottom-up; reverse so the largest share sits on top.
    const ordered = [...visibleRows].reverse();

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow', shadowStyle: { color: chrome.splitLine, opacity: 0.4 } },
        backgroundColor: chrome.tooltipBg,
        borderColor: chrome.tooltipBorder,
        borderWidth: 1,
        extraCssText: 'direction: rtl; text-align: right;',
        textStyle: { color: chrome.tooltipText, fontSize: 12, fontFamily: PANEL_FONT_FAMILY },
        formatter: (params: Array<{ dataIndex: number }>) => {
          if (!params.length) return '';
          const row = ordered[params[0].dataIndex];
          return `<div style="font-family:${PANEL_FONT_FAMILY};font-size:12px;direction:rtl;text-align:right;color:${chrome.tooltipText}">
            <div style="font-weight:600">${escapeHtml(row.name)}${row.key === 'own' && row.isFocus ? ' (شما)' : ''}</div>
            <div style="margin-top:4px">سهم از دیده‌شدن: <strong>${row.shareOfVoice}%</strong></div></div>`;
        },
      },
      grid: { left: 84, right: 16, top: 12, bottom: 32, containLabel: false },
      xAxis: {
        type: 'value',
        min: 0,
        max: axisMax,
        splitNumber: 4,
        splitLine: { lineStyle: { color: chrome.splitLine } },
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: chrome.axisLabel,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          formatter: '{value}%',
        },
      },
      yAxis: {
        type: 'category',
        data: ordered.map((row) => (row.isFocus ? `{own|${row.name}}` : row.name)),
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          show: true,
          interval: 0,
          hideOverlap: false,
          align: 'left',
          margin: 82,
          color: chrome.axisLabel,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          overflow: 'truncate',
          width: 80,
          rich: {
            own: {
              color: primary,
              fontWeight: 'bold',
              fontSize: 11,
              fontFamily: PANEL_FONT_FAMILY,
              width: 80,
              align: 'left',
            },
          },
        },
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 18,
          barCategoryGap: '35%',
          itemStyle: { borderRadius: [0, 4, 4, 0] },
          data: ordered.map((row) => ({
            value: row.shareOfVoice,
            itemStyle: { color: barFill(row.shareOfVoice, maxShare, primary) },
          })),
        },
      ],
    };
  }, [rows, visibleRows, isDark, themeVariant, plotHeight]);

  const primary = themePrimary();
  const maxShare = Math.max(0, ...rows.map((row) => row.shareOfVoice));

  return (
    <div className="d-flex flex-column h-100">
      <div className="flex-grow-1 d-flex align-items-center" dir="ltr">
        {option ? (
          <ReactECharts
            key={`${isDark ? 'dark' : 'light'}-${themeVariant ?? 'primary'}`}
            option={option}
            notMerge
            className="w-100"
            style={{ height: plotHeight }}
            opts={{ renderer: 'svg' }}
          />
        ) : (
          <DashboardEmptyNote minHeight={plotHeight}>
            همه برندها از نمودار پنهان شده‌اند.
          </DashboardEmptyNote>
        )}
      </div>
      <ChartLegend
        onToggle={toggle}
        items={rows.map((row) => ({
          id: row.key,
          label: row.name,
          color: barFill(row.shareOfVoice, maxShare, primary),
          bold: row.isFocus,
          hidden: hidden.has(row.key),
        }))}
      />
    </div>
  );
}
